import { Box, Flex, FormControl, FormLabel, Select, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import type { GenePanelConfigurationId } from '@resistapp/common/gene-panels';

type PipettingVolumes = {
  sample: {
    mastermix2x: number;
    water: number;
    antifoamB: number;
    glycerol50: number;
    mixPerWell: number;
    reagentPerWell: number;
  };
  primer: {
    mastermix2x: number;
    water: number;
    antifoamB: number;
    glycerol50: number;
    mixPerWell: number;
    reagentPerWell: number;
  };
};

type TotalMixKey = 'mastermix2x' | 'water' | 'antifoamB' | 'glycerol50';
type PerWellKey = 'mixPerWell' | 'reagentPerWell';

type PipettingTotals = {
  sample: Record<TotalMixKey, number>;
  primer: Record<TotalMixKey, number>;
};

type PipettingFieldDefinition<Key extends keyof PipettingVolumes['sample']> = {
  key: Key;
  label: string;
};

const TOTAL_MIX_FIELDS: ReadonlyArray<PipettingFieldDefinition<TotalMixKey>> = [
  { key: 'mastermix2x', label: 'Mastermix (2X)' },
  { key: 'water', label: 'Water' },
  { key: 'antifoamB', label: 'Antifoam B (1%)' },
  { key: 'glycerol50', label: 'Glycerol (50%)' },
] as const;

const PER_WELL_FIELDS_BY_STEP: Readonly<
  Record<'sample' | 'primer', ReadonlyArray<PipettingFieldDefinition<PerWellKey>>>
> = {
  sample: [
    { key: 'mixPerWell', label: 'Sample mix per well' },
    { key: 'reagentPerWell', label: 'Sample per well' },
  ],
  primer: [
    { key: 'mixPerWell', label: 'Primer mix per well' },
    { key: 'reagentPerWell', label: 'Assay per well' },
  ],
} as const;

const CONFIGURATION_BY_SAMPLES_PER_CHIP: Readonly<Record<number, GenePanelConfigurationId>> = {
  4: '384:4',
  5: '296:5',
  6: '248:6',
  8: '216:8',
  12: '144:12',
  14: '120:14',
  18: '96:18',
  21: '80:21',
  24: '72:24',
  32: '54:32',
  36: '48:36',
  48: '36:48',
  72: '24:72',
  128: '12:128',
  256: '6:256',
} as const;

// Source values copied from repo root CSV files:
// - SmartChip pipetting volumes - Pipetting volumes MyDesign MSND.csv (MSND)
// - SmartChip pipetting volumes - Pipetting volumes MyDesign ND.csv (SCND)
const PIPETTING_VOLUMES_BY_SYSTEM: Readonly<
  Record<PipettingSystem, Record<GenePanelConfigurationId, PipettingVolumes>>
> = {
  SCND: {
    '6:256': {
      sample: { mastermix2x: 5.0, water: 0.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 6.0, reagentPerWell: 4.0 },
      primer: { mastermix2x: 5.9, water: 3.2, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 9.4, reagentPerWell: 2.3 },
    },
    '12:128': {
      sample: { mastermix2x: 5.0, water: 0.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 6.0, reagentPerWell: 4.0 },
      primer: { mastermix2x: 5.9, water: 3.2, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 9.4, reagentPerWell: 2.3 },
    },
    '24:72': {
      sample: { mastermix2x: 5.0, water: 0.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 6.0, reagentPerWell: 4.0 },
      primer: { mastermix2x: 6.2, water: 3.3, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 9.8, reagentPerWell: 2.5 },
    },
    '36:48': {
      sample: { mastermix2x: 5.0, water: 0.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 6.0, reagentPerWell: 4.0 },
      primer: { mastermix2x: 7.1, water: 3.8, antifoamB: 0.1, glycerol50: 0.3, mixPerWell: 11.3, reagentPerWell: 2.8 },
    },
    '48:36': {
      sample: { mastermix2x: 5.0, water: 0.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 6.0, reagentPerWell: 4.0 },
      primer: { mastermix2x: 6.2, water: 3.3, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 9.8, reagentPerWell: 2.5 },
    },
    '54:32': {
      sample: { mastermix2x: 5.0, water: 0.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 6.0, reagentPerWell: 4.0 },
      primer: { mastermix2x: 5.9, water: 3.2, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 9.4, reagentPerWell: 2.3 },
    },
    '72:24': {
      sample: { mastermix2x: 5.3, water: 0.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 6.3, reagentPerWell: 4.2 },
      primer: { mastermix2x: 5.3, water: 2.8, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 8.4, reagentPerWell: 2.1 },
    },
    '80:21': {
      sample: { mastermix2x: 5.5, water: 0.8, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 6.5, reagentPerWell: 4.4 },
      primer: { mastermix2x: 5.1, water: 2.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 8.1, reagentPerWell: 2.0 },
    },
    '96:18': {
      sample: { mastermix2x: 5.9, water: 0.8, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 7.0, reagentPerWell: 4.7 },
      primer: { mastermix2x: 5.0, water: 2.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 8.0, reagentPerWell: 2.0 },
    },
    '120:14': {
      sample: { mastermix2x: 6.5, water: 0.9, antifoamB: 0.1, glycerol50: 0.3, mixPerWell: 7.7, reagentPerWell: 5.2 },
      primer: { mastermix2x: 5.0, water: 2.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 8.0, reagentPerWell: 2.0 },
    },
    '144:12': {
      sample: { mastermix2x: 7.1, water: 1.0, antifoamB: 0.1, glycerol50: 0.3, mixPerWell: 8.5, reagentPerWell: 5.6 },
      primer: { mastermix2x: 5.0, water: 2.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 8.0, reagentPerWell: 2.0 },
    },
    '216:8': {
      sample: { mastermix2x: 6.2, water: 0.9, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 7.4, reagentPerWell: 4.9 },
      primer: { mastermix2x: 5.0, water: 2.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 8.0, reagentPerWell: 2.0 },
    },
    '248:6': {
      sample: { mastermix2x: 6.6, water: 0.9, antifoamB: 0.1, glycerol50: 0.3, mixPerWell: 7.9, reagentPerWell: 5.2 },
      primer: { mastermix2x: 5.0, water: 2.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 8.0, reagentPerWell: 2.0 },
    },
    '296:5': {
      sample: { mastermix2x: 5.3, water: 0.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 6.4, reagentPerWell: 4.2 },
      primer: { mastermix2x: 5.0, water: 2.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 8.0, reagentPerWell: 2.0 },
    },
    '384:4': {
      sample: { mastermix2x: 5.9, water: 0.8, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 7.0, reagentPerWell: 4.7 },
      primer: { mastermix2x: 5.0, water: 2.7, antifoamB: 0.1, glycerol50: 0.2, mixPerWell: 8.0, reagentPerWell: 2.0 },
    },
  },
  MSND: {
    '6:256': {
      sample: { mastermix2x: 5.8, water: 0.9, antifoamB: 0.0, glycerol50: 0.2, mixPerWell: 7.0, reagentPerWell: 4.7 },
      primer: { mastermix2x: 8.9, water: 1.4, antifoamB: 0.0, glycerol50: 0.4, mixPerWell: 10.7, reagentPerWell: 7.2 },
    },
    '12:128': {
      sample: { mastermix2x: 5.8, water: 0.9, antifoamB: 0.0, glycerol50: 0.2, mixPerWell: 7.0, reagentPerWell: 4.7 },
      primer: { mastermix2x: 8.9, water: 1.4, antifoamB: 0.0, glycerol50: 0.4, mixPerWell: 10.7, reagentPerWell: 7.2 },
    },
    '24:72': {
      sample: { mastermix2x: 6.2, water: 1.0, antifoamB: 0.0, glycerol50: 0.2, mixPerWell: 7.4, reagentPerWell: 5.0 },
      primer: { mastermix2x: 8.9, water: 1.4, antifoamB: 0.0, glycerol50: 0.4, mixPerWell: 10.7, reagentPerWell: 7.2 },
    },
    '36:48': {
      sample: { mastermix2x: 6.5, water: 1.0, antifoamB: 0.0, glycerol50: 0.3, mixPerWell: 7.8, reagentPerWell: 5.2 },
      primer: { mastermix2x: 10.2, water: 1.6, antifoamB: 0.0, glycerol50: 0.4, mixPerWell: 12.2, reagentPerWell: 8.1 },
    },
    '48:36': {
      sample: { mastermix2x: 7.0, water: 1.1, antifoamB: 0.0, glycerol50: 0.3, mixPerWell: 8.4, reagentPerWell: 5.6 },
      primer: { mastermix2x: 8.9, water: 1.4, antifoamB: 0.0, glycerol50: 0.4, mixPerWell: 10.7, reagentPerWell: 7.2 },
    },
    '54:32': {
      sample: { mastermix2x: 7.2, water: 1.1, antifoamB: 0.0, glycerol50: 0.3, mixPerWell: 8.6, reagentPerWell: 5.8 },
      primer: { mastermix2x: 8.5, water: 1.4, antifoamB: 0.0, glycerol50: 0.3, mixPerWell: 10.2, reagentPerWell: 6.8 },
    },
    '72:24': {
      sample: { mastermix2x: 7.8, water: 1.2, antifoamB: 0.0, glycerol50: 0.3, mixPerWell: 9.3, reagentPerWell: 6.2 },
      primer: { mastermix2x: 7.8, water: 1.2, antifoamB: 0.0, glycerol50: 0.3, mixPerWell: 9.3, reagentPerWell: 6.2 },
    },
    '80:21': {
      sample: { mastermix2x: 8.0, water: 1.3, antifoamB: 0.0, glycerol50: 0.3, mixPerWell: 9.6, reagentPerWell: 6.4 },
      primer: { mastermix2x: 7.5, water: 1.2, antifoamB: 0.0, glycerol50: 0.3, mixPerWell: 9.0, reagentPerWell: 6.0 },
    },
    '96:18': {
      sample: { mastermix2x: 8.5, water: 1.4, antifoamB: 0.0, glycerol50: 0.3, mixPerWell: 10.2, reagentPerWell: 6.8 },
      primer: { mastermix2x: 7.2, water: 1.1, antifoamB: 0.0, glycerol50: 0.3, mixPerWell: 8.6, reagentPerWell: 5.8 },
    },
    '120:14': {
      sample: { mastermix2x: 9.5, water: 1.5, antifoamB: 0.0, glycerol50: 0.4, mixPerWell: 11.4, reagentPerWell: 7.6 },
      primer: { mastermix2x: 6.8, water: 1.1, antifoamB: 0.0, glycerol50: 0.3, mixPerWell: 8.1, reagentPerWell: 5.4 },
    },
    '144:12': {
      sample: { mastermix2x: 10.2, water: 1.6, antifoamB: 0.0, glycerol50: 0.4, mixPerWell: 12.2, reagentPerWell: 8.1 },
      primer: { mastermix2x: 6.5, water: 1.0, antifoamB: 0.0, glycerol50: 0.3, mixPerWell: 7.8, reagentPerWell: 5.2 },
    },
    '216:8': {
      sample: { mastermix2x: 8.9, water: 1.4, antifoamB: 0.0, glycerol50: 0.4, mixPerWell: 10.7, reagentPerWell: 7.2 },
      primer: { mastermix2x: 6.2, water: 1.0, antifoamB: 0.0, glycerol50: 0.2, mixPerWell: 7.4, reagentPerWell: 5.0 },
    },
    '248:6': {
      sample: { mastermix2x: 9.7, water: 1.5, antifoamB: 0.0, glycerol50: 0.4, mixPerWell: 11.6, reagentPerWell: 7.8 },
      primer: { mastermix2x: 6.1, water: 1.0, antifoamB: 0.0, glycerol50: 0.2, mixPerWell: 7.3, reagentPerWell: 4.9 },
    },
    '296:5': {
      sample: { mastermix2x: 8.0, water: 1.3, antifoamB: 0.0, glycerol50: 0.3, mixPerWell: 9.6, reagentPerWell: 6.4 },
      primer: { mastermix2x: 6.0, water: 1.0, antifoamB: 0.0, glycerol50: 0.2, mixPerWell: 7.2, reagentPerWell: 4.8 },
    },
    '384:4': {
      sample: { mastermix2x: 8.9, water: 1.4, antifoamB: 0.0, glycerol50: 0.4, mixPerWell: 10.7, reagentPerWell: 7.2 },
      primer: { mastermix2x: 5.8, water: 0.9, antifoamB: 0.0, glycerol50: 0.2, mixPerWell: 7.0, reagentPerWell: 4.7 },
    },
  },
};

const PIPETTING_TOTALS_BY_SYSTEM: Readonly<Record<PipettingSystem, Record<GenePanelConfigurationId, PipettingTotals>>> =
  {
    SCND: {
      '6:256': {
        sample: {
          mastermix2x: 4608.0,
          water: 645.1,
          antifoamB: 92.2,
          glycerol50: 184.3,
        },
        primer: {
          mastermix2x: 337.0,
          water: 182.0,
          antifoamB: 6.7,
          glycerol50: 13.5,
        },
      },
      '12:128': {
        sample: {
          mastermix2x: 2304.0,
          water: 322.6,
          antifoamB: 46.1,
          glycerol50: 92.2,
        },
        primer: {
          mastermix2x: 337.0,
          water: 182.0,
          antifoamB: 6.7,
          glycerol50: 13.5,
        },
      },
      '24:72': {
        sample: {
          mastermix2x: 1296.0,
          water: 181.4,
          antifoamB: 25.9,
          glycerol50: 51.8,
        },
        primer: {
          mastermix2x: 354.2,
          water: 191.2,
          antifoamB: 7.1,
          glycerol50: 14.2,
        },
      },
      '36:48': {
        sample: {
          mastermix2x: 864.0,
          water: 121.0,
          antifoamB: 17.3,
          glycerol50: 34.6,
        },
        primer: {
          mastermix2x: 304.6,
          water: 164.6,
          antifoamB: 6.1,
          glycerol50: 12.2,
        },
      },
      '48:36': {
        sample: {
          mastermix2x: 648.0,
          water: 90.7,
          antifoamB: 13.0,
          glycerol50: 25.9,
        },
        primer: {
          mastermix2x: 354.2,
          water: 191.2,
          antifoamB: 7.1,
          glycerol50: 14.2,
        },
      },
      '54:32': {
        sample: {
          mastermix2x: 576.0,
          water: 80.6,
          antifoamB: 11.5,
          glycerol50: 23.0,
        },
        primer: {
          mastermix2x: 379.1,
          water: 204.8,
          antifoamB: 7.6,
          glycerol50: 15.2,
        },
      },
      '72:24': {
        sample: {
          mastermix2x: 453.6,
          water: 63.1,
          antifoamB: 9.1,
          glycerol50: 18.1,
        },
        primer: {
          mastermix2x: 453.6,
          water: 245.4,
          antifoamB: 9.1,
          glycerol50: 18.1,
        },
      },
      '80:21': {
        sample: {
          mastermix2x: 418.6,
          water: 58.4,
          antifoamB: 8.4,
          glycerol50: 16.7,
        },
        primer: {
          mastermix2x: 484.8,
          water: 262.1,
          antifoamB: 9.7,
          glycerol50: 19.4,
        },
      },
      '96:18': {
        sample: {
          mastermix2x: 382.3,
          water: 53.8,
          antifoamB: 7.6,
          glycerol50: 15.3,
        },
        primer: {
          mastermix2x: 576.0,
          water: 311.0,
          antifoamB: 11.5,
          glycerol50: 23.0,
        },
      },
      '120:14': {
        sample: {
          mastermix2x: 325.1,
          water: 45.4,
          antifoamB: 6.5,
          glycerol50: 13.0,
        },
        primer: {
          mastermix2x: 720.0,
          water: 388.8,
          antifoamB: 14.4,
          glycerol50: 28.8,
        },
      },
      '144:12': {
        sample: {
          mastermix2x: 304.6,
          water: 42.8,
          antifoamB: 6.1,
          glycerol50: 12.2,
        },
        primer: {
          mastermix2x: 864.0,
          water: 466.6,
          antifoamB: 17.3,
          glycerol50: 34.6,
        },
      },
      '216:8': {
        sample: {
          mastermix2x: 354.2,
          water: 49.5,
          antifoamB: 7.1,
          glycerol50: 14.2,
        },
        primer: {
          mastermix2x: 1296.0,
          water: 699.8,
          antifoamB: 25.9,
          glycerol50: 51.8,
        },
      },
      '248:6': {
        sample: {
          mastermix2x: 314.4,
          water: 44.2,
          antifoamB: 6.3,
          glycerol50: 12.6,
        },
        primer: {
          mastermix2x: 1488.0,
          water: 803.5,
          antifoamB: 29.8,
          glycerol50: 59.5,
        },
      },
      '296:5': {
        sample: {
          mastermix2x: 407.0,
          water: 56.8,
          antifoamB: 8.1,
          glycerol50: 16.1,
        },
        primer: {
          mastermix2x: 1776.0,
          water: 959.0,
          antifoamB: 35.5,
          glycerol50: 71.0,
        },
      },
      '384:4': {
        sample: {
          mastermix2x: 339.8,
          water: 47.8,
          antifoamB: 6.8,
          glycerol50: 13.6,
        },
        primer: {
          mastermix2x: 2304.0,
          water: 1244.2,
          antifoamB: 46.1,
          glycerol50: 92.2,
        },
      },
    },
    MSND: {
      '6:256': {
        sample: {
          mastermix2x: 5382.1,
          water: 853.4,
          antifoamB: 0.0,
          glycerol50: 215.7,
        },
        primer: {
          mastermix2x: 514.4,
          water: 81.3,
          antifoamB: 0.0,
          glycerol50: 20.6,
        },
      },
      '12:128': {
        sample: {
          mastermix2x: 2691.1,
          water: 426.7,
          antifoamB: 0.0,
          glycerol50: 107.8,
        },
        primer: {
          mastermix2x: 514.4,
          water: 81.3,
          antifoamB: 0.0,
          glycerol50: 20.6,
        },
      },
      '24:72': {
        sample: {
          mastermix2x: 1599.3,
          water: 254.5,
          antifoamB: 0.0,
          glycerol50: 64.3,
        },
        primer: {
          mastermix2x: 514.4,
          water: 81.3,
          antifoamB: 0.0,
          glycerol50: 20.6,
        },
      },
      '36:48': {
        sample: {
          mastermix2x: 1124.9,
          water: 178.0,
          antifoamB: 0.0,
          glycerol50: 44.9,
        },
        primer: {
          mastermix2x: 439.8,
          water: 69.7,
          antifoamB: 0.0,
          glycerol50: 17.5,
        },
      },
      '48:36': {
        sample: {
          mastermix2x: 908.5,
          water: 143.9,
          antifoamB: 0.0,
          glycerol50: 36.3,
        },
        primer: {
          mastermix2x: 514.4,
          water: 81.3,
          antifoamB: 0.0,
          glycerol50: 20.6,
        },
      },
      '54:32': {
        sample: {
          mastermix2x: 826.0,
          water: 131.6,
          antifoamB: 0.0,
          glycerol50: 33.2,
        },
        primer: {
          mastermix2x: 551.4,
          water: 87.5,
          antifoamB: 0.0,
          glycerol50: 22.0,
        },
      },
      '72:24': {
        sample: {
          mastermix2x: 670.5,
          water: 106.3,
          antifoamB: 0.0,
          glycerol50: 26.8,
        },
        primer: {
          mastermix2x: 670.5,
          water: 106.3,
          antifoamB: 0.0,
          glycerol50: 26.8,
        },
      },
      '80:21': {
        sample: {
          mastermix2x: 615.2,
          water: 97.5,
          antifoamB: 0.0,
          glycerol50: 24.6,
        },
        primer: {
          mastermix2x: 721.0,
          water: 114.2,
          antifoamB: 0.0,
          glycerol50: 28.8,
        },
      },
      '96:18': {
        sample: {
          mastermix2x: 551.4,
          water: 87.5,
          antifoamB: 0.0,
          glycerol50: 22.0,
        },
        primer: {
          mastermix2x: 826.0,
          water: 131.6,
          antifoamB: 0.0,
          glycerol50: 33.2,
        },
      },
      '120:14': {
        sample: {
          mastermix2x: 479.3,
          water: 76.1,
          antifoamB: 0.0,
          glycerol50: 19.2,
        },
        primer: {
          mastermix2x: 973.4,
          water: 154.1,
          antifoamB: 0.0,
          glycerol50: 38.9,
        },
      },
      '144:12': {
        sample: {
          mastermix2x: 439.8,
          water: 69.7,
          antifoamB: 0.0,
          glycerol50: 17.5,
        },
        primer: {
          mastermix2x: 1124.9,
          water: 178.0,
          antifoamB: 0.0,
          glycerol50: 44.9,
        },
      },
      '216:8': {
        sample: {
          mastermix2x: 514.4,
          water: 81.3,
          antifoamB: 0.0,
          glycerol50: 20.6,
        },
        primer: {
          mastermix2x: 1599.3,
          water: 254.5,
          antifoamB: 0.0,
          glycerol50: 64.3,
        },
      },
      '248:6': {
        sample: {
          mastermix2x: 464.6,
          water: 73.5,
          antifoamB: 0.0,
          glycerol50: 18.6,
        },
        primer: {
          mastermix2x: 1812.4,
          water: 287.5,
          antifoamB: 0.0,
          glycerol50: 72.6,
        },
      },
      '296:5': {
        sample: {
          mastermix2x: 615.2,
          water: 97.5,
          antifoamB: 0.0,
          glycerol50: 24.6,
        },
        primer: {
          mastermix2x: 2134.8,
          water: 337.4,
          antifoamB: 0.0,
          glycerol50: 85.2,
        },
      },
      '384:4': {
        sample: {
          mastermix2x: 514.4,
          water: 81.3,
          antifoamB: 0.0,
          glycerol50: 20.6,
        },
        primer: {
          mastermix2x: 2691.1,
          water: 426.7,
          antifoamB: 0.0,
          glycerol50: 107.8,
        },
      },
    },
  };

function resolveConfigurationId(
  configurationId: GenePanelConfigurationId | null,
  samplesPerChip: number | null,
): GenePanelConfigurationId | null {
  if (configurationId && configurationId in PIPETTING_VOLUMES_BY_SYSTEM.MSND) {
    return configurationId;
  }
  if (samplesPerChip == null) {
    return null;
  }
  return CONFIGURATION_BY_SAMPLES_PER_CHIP[samplesPerChip] ?? null;
}

function formatVolume(value: number): string {
  return value.toFixed(1);
}

export type PipettingSystem = 'MSND' | 'SCND';

type SmartChipPipettingVolumesProps = {
  configurationId: GenePanelConfigurationId | null;
  samplesPerChip: number | null;
  step: 'sample' | 'primer';
  system: PipettingSystem;
  onSystemChange?: (nextSystem: PipettingSystem) => void;
  showDispensingSystemSelector?: boolean;
};

export function SmartChipPipettingVolumes({
  configurationId,
  samplesPerChip,
  step,
  system,
  onSystemChange,
  showDispensingSystemSelector = true,
}: SmartChipPipettingVolumesProps) {
  const resolvedConfigurationId = resolveConfigurationId(configurationId, samplesPerChip);
  const baseValues =
    resolvedConfigurationId != null ? PIPETTING_VOLUMES_BY_SYSTEM[system][resolvedConfigurationId][step] : null;
  const totalValues =
    resolvedConfigurationId != null ? PIPETTING_TOTALS_BY_SYSTEM[system][resolvedConfigurationId][step] : null;
  const values = baseValues && totalValues ? { ...baseValues, ...totalValues } : null;
  const showAntifoam = system === 'SCND';
  const totalMixFields = showAntifoam ? TOTAL_MIX_FIELDS : TOTAL_MIX_FIELDS.filter(field => field.key !== 'antifoamB');
  const perWellFields = PER_WELL_FIELDS_BY_STEP[step];
  const totalMixTitle = step === 'primer' ? 'Total Primer reaction mix' : 'Total Sample reaction mix';
  const perWellTitle = step === 'primer' ? 'Primer reaction mix per well' : 'Sample reaction mix per well';
  const desktopColumnCount = showAntifoam ? 4 : 3;
  const configurationPostfix = resolvedConfigurationId ? `${system} ${resolvedConfigurationId}` : system;

  return (
    <VStack align="stretch" spacing={3}>
      {showDispensingSystemSelector ? (
        <Flex align={{ base: 'stretch', md: 'flex-end' }} direction={{ base: 'column', md: 'row' }} gap={3}>
          <FormControl maxW={{ base: 'full', md: '220px' }}>
            <FormLabel mb={1} fontSize="sm" fontWeight="medium">
              Dispensing system
            </FormLabel>
            <Select
              size="sm"
              value={system}
              onChange={event => {
                const value = event.target.value === 'SCND' ? 'SCND' : 'MSND';
                onSystemChange?.(value);
              }}
              isDisabled={!onSystemChange}
            >
              <option value="MSND">MSND</option>
              <option value="SCND">SCND</option>
            </Select>
          </FormControl>
        </Flex>
      ) : null}

      {values ? (
        <VStack align="stretch" spacing={4}>
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              {totalMixTitle}
              <Text as="span" ml={1} fontWeight="normal" color="gray.500">
                — {configurationPostfix}
              </Text>
            </Text>
            <SimpleGrid columns={{ base: 2, md: desktopColumnCount }} spacing={3}>
              {totalMixFields.map(field => (
                <Box key={field.label} borderWidth="1px" borderRadius="md" bg="gray.50" px={3} py={2}>
                  <Text fontSize="xs" color="gray.600">
                    {field.label}
                  </Text>
                  <Text fontSize="md" fontWeight="semibold" color="gray.800">
                    {formatVolume(values[field.key])} µL
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>

          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              {perWellTitle}
            </Text>
            <SimpleGrid columns={{ base: 2, md: desktopColumnCount }} spacing={3}>
              {perWellFields.map(field => (
                <Box key={field.label} borderWidth="1px" borderRadius="md" bg="gray.50" px={3} py={2}>
                  <Text fontSize="xs" color="gray.600">
                    {field.label}
                  </Text>
                  <Text fontSize="md" fontWeight="semibold" color="gray.800">
                    {formatVolume(values[field.key])} µL
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </VStack>
      ) : (
        <Box borderWidth="1px" borderRadius="md" px={3} py={2} bg="gray.50">
          <Text fontSize="sm" color="gray.600">
            Pipetting values are not available for this configuration.
          </Text>
        </Box>
      )}
    </VStack>
  );
}
